import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/ui/PageHeader';
import { unknownWordsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, STALE } from '../hooks/useQuery';
import { setQueryData, makeKey } from '../lib/queryCache';
import {
  FlashcardGridSkeleton,
  TableRowsSkeleton,
  Skeleton,
  ErrorState,
} from '../components/ui/Skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import SpeakButton from '../components/ui/SpeakButton';
import {
  Bookmark,
  Search,
  Trash2,
  RotateCw,
  ListFilter,
  BookOpen,
  Volume2,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

const STATUS_FILTERS = [
  { id: 'all', label: 'Të gjitha' },
  { id: 'new', label: 'E re' },
  { id: 'learning', label: 'Duke mësuar' },
  { id: 'known', label: 'E mësuar' },
];

const STATUS_LABELS = {
  new: 'E re',
  learning: 'Duke mësuar',
  known: 'E mësuar',
};

const Flashcard = ({ item, updateStatus, remove, busyId }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <div
      onClick={() => setFlipped(!flipped)}
      className="h-52 w-full cursor-pointer select-none"
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full duration-500 rounded-2xl shadow-sm border border-zinc-200/80 bg-white transition-all transform-gpu"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Side */}
        <div
          className="absolute inset-0 w-full h-full p-5 flex flex-col justify-between rounded-2xl bg-white overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div aria-hidden="true" className="absolute top-0 inset-x-0 flex h-1">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          <div className="flex justify-between items-start pt-1">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400">Gjermanisht</span>
            {item.level && (
              <span className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-extrabold text-zinc-700">
                {item.level.toUpperCase()}
              </span>
            )}
          </div>
          <div className="my-auto flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-xl font-black text-zinc-900 text-center select-all">{item.word}</span>
            <SpeakButton text={item.word} lang="de-DE" />
          </div>
          <div className="text-[11px] text-zinc-400 text-center font-medium">Kliko për ta rrotulluar</div>
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 w-full h-full p-5 flex flex-col justify-between rounded-2xl bg-zinc-950 text-white overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div aria-hidden="true" className="absolute top-0 inset-x-0 flex h-1">
            <span className="flex-1 bg-zinc-800" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          <div className="flex justify-between items-start pt-1">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400">Përkthimi Shqip</span>
            <span className="text-[10px] text-zinc-500">Kliko për të kthyer</span>
          </div>
          <div className="my-auto text-center space-y-1.5 px-2">
            <p className="text-base font-extrabold text-amber-400">{item.context || '—'}</p>
            {item.notes && <p className="text-xs text-zinc-300 italic">Shënim: {item.notes}</p>}
          </div>
          <div className="flex justify-between items-center" onClick={(e) => e.stopPropagation()}>
            <select
              value={item.status}
              disabled={busyId === item.id}
              onChange={(e) => updateStatus(item.id, e.target.value)}
              className="text-xs border border-zinc-800 bg-zinc-900 text-white rounded-lg px-2.5 py-1.5 focus:outline-none cursor-pointer"
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value} className="bg-zinc-950 text-white">
                  {label}
                </option>
              ))}
            </select>
            <button
              disabled={busyId === item.id}
              onClick={() => remove(item.id)}
              className="p-1.5 text-zinc-400 hover:text-red-400 rounded-lg hover:bg-zinc-900 transition-colors"
              title="Fshi"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnknownWordsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'list' or 'cards'

  const {
    data: fetchedItems,
    isLoading,
    isRefreshing,
    error: queryError,
    refetch,
  } = useQuery(
    'unknown_words',
    'list',
    async () => {
      const res = await unknownWordsAPI.list();
      return res.data?.items || [];
    },
    { staleTime: STALE.medium }
  );

  const [localItems, setLocalItems] = useState(null);

  useEffect(() => {
    if (fetchedItems) {
      setLocalItems(fetchedItems);
    }
  }, [fetchedItems]);

  const items = useMemo(() => localItems || fetchedItems || [], [localItems, fetchedItems]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, learning: 0, known: 0 };
    items.forEach((it) => {
      if (c[it.status] !== undefined) c[it.status] += 1;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== 'all' && it.status !== statusFilter) return false;
      if (!q) return true;
      return (
        it.word.toLowerCase().includes(q) ||
        (it.context || '').toLowerCase().includes(q) ||
        (it.notes || '').toLowerCase().includes(q)
      );
    });
  }, [items, statusFilter, search]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    setActionError(null);
    const previous = items;
    const optimistic = items.map((it) => (it.id === id ? { ...it, status } : it));
    setLocalItems(optimistic);
    if (user?.id) {
      const key = makeKey(user.id, 'unknown_words', 'list');
      setQueryData(key, optimistic);
    }
    try {
      const res = await unknownWordsAPI.update(id, { status });
      const updated = items.map((it) => (it.id === id ? { ...it, ...res.data } : it));
      setLocalItems(updated);
      if (user?.id) {
        const key = makeKey(user.id, 'unknown_words', 'list');
        setQueryData(key, updated);
      }
    } catch {
      setActionError('Nuk u përditësua statusi.');
      setLocalItems(previous);
      if (user?.id) {
        const key = makeKey(user.id, 'unknown_words', 'list');
        setQueryData(key, previous);
      }
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    setBusyId(id);
    setActionError(null);
    const previous = items;
    const optimistic = items.filter((it) => it.id !== id);
    setLocalItems(optimistic);
    if (user?.id) {
      const key = makeKey(user.id, 'unknown_words', 'list');
      setQueryData(key, optimistic);
    }
    try {
      await unknownWordsAPI.remove(id);
    } catch {
      setActionError('Nuk u fshi fjala.');
      setLocalItems(previous);
      if (user?.id) {
        const key = makeKey(user.id, 'unknown_words', 'list');
        setQueryData(key, previous);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Page Header */}
        <PageHeader
          icon={CheckCircle2}
          badge="Fjalor & Përparim"
          title="Fjalët e Ditura"
          description="Këtu gjenden fjalët që ke shënuar si të ditura në fjalor. Mund t'i rishikosh me karta, t'u dëgjosh shqiptimin, ose t'i heqësh nga të diturat nëse dëshiron t'i mësosh sërish."
        >
          <div className="flex flex-wrap gap-2">
            {isLoading ? (
              <>
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-6 w-28 rounded-full" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </>
            ) : (
              <>
                <span className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-xs font-bold text-zinc-700">
                  {counts.all} gjithsej
                </span>
                <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800">
                  {counts.known} të mësuara
                </span>
                <span className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-800">
                  {counts.learning} në përsëritje
                </span>
                <span className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">
                  {counts.new} të reja
                </span>
              </>
            )}
          </div>
        </PageHeader>

        {/* Main Content Box */}
        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-5 sm:p-7 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-zinc-100 pb-5">
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Rishiko Fjalët</h2>
              <p className="text-xs text-zinc-500">Zgjidh një fjalë, provo ta kujtosh dhe kontrollo përkthimin.</p>
            </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="inline-flex rounded-xl border border-zinc-200/80 bg-zinc-100/70 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                    viewMode === 'cards'
                      ? 'bg-white text-zinc-950 shadow-2xs'
                      : 'text-zinc-600 hover:text-zinc-950'
                  }`}
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Karta
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                    viewMode === 'list'
                      ? 'bg-white text-zinc-950 shadow-2xs'
                      : 'text-zinc-600 hover:text-zinc-950'
                  }`}
                >
                  <ListFilter className="w-3.5 h-3.5" />
                  Listë
                </button>
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isLoading || isRefreshing}
                className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
              >
                <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                Rifresko
              </button>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                    statusFilter === f.id
                      ? 'bg-zinc-950 text-white shadow-2xs'
                      : 'border border-zinc-200/80 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
                  }`}
                >
                  {f.label} ({counts[f.id] ?? 0})
                </button>
              ))}
            </div>

            <div className="relative w-full md:max-w-xs">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Kërko fjalë ose përkthim..."
                className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-[#DD0000] focus:outline-none focus:ring-1 focus:ring-[#DD0000]"
              />
            </div>
          </div>

          {(actionError || (queryError && !items.length)) && (
            <ErrorState
              message={actionError || 'Nuk u ngarkuan fjalët.'}
              onRetry={queryError ? () => refetch() : undefined}
            />
          )}

          {isLoading ? (
            viewMode === 'cards' ? (
              <FlashcardGridSkeleton count={6} />
            ) : (
              <TableRowsSkeleton rows={6} />
            )
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30 text-emerald-500" />
              <p className="text-sm font-bold text-zinc-800 mb-1">
                {items.length === 0 ? 'Nuk ke asnjë fjalë të ditur ende' : 'Asnjë rezultat për këtë filtër'}
              </p>
              <p className="text-xs text-zinc-500 mb-5">
                Shko te fjalori dhe shëno fjalët që i di me butonin &quot;E di&quot; për t’i ruajtur këtu.
              </p>
              <button
                type="button"
                onClick={() => navigate('/vocabulary')}
                className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2.5 text-xs font-bold text-white hover:bg-[#DD0000] transition"
              >
                Shko te Fjalori
              </button>
            </div>
          ) : viewMode === 'cards' ? (
            /* Cards View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((it) => (
                <Flashcard
                  key={it.id}
                  item={it}
                  updateStatus={updateStatus}
                  remove={remove}
                  busyId={busyId}
                />
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="space-y-3">
              <div className="text-[11px] text-zinc-400 italic">
                * Këshillë: Kaloni kursorin mbi pjesën e mjegulluar për ta zbuluar përkthimin shqip.
              </div>
              <div className="overflow-x-auto rounded-xl border border-zinc-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50/70 hover:bg-zinc-50/70 text-xs">
                      <TableHead className="w-[30%] font-bold text-zinc-700">Fjala (Gjermanisht)</TableHead>
                      <TableHead className="w-[35%] font-bold text-zinc-700">Përkthimi</TableHead>
                      <TableHead className="w-[20%] font-bold text-zinc-700">Statusi</TableHead>
                      <TableHead className="w-[15%] text-right font-bold text-zinc-700">Veprim</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((it) => (
                      <TableRow key={it.id} className="hover:bg-zinc-50/50 text-xs">
                        <TableCell className="font-bold text-zinc-900">
                          <div className="flex items-center gap-2">
                            <span>{it.word}</span>
                            <SpeakButton text={it.word} lang="de-DE" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className="blur-[4px] hover:blur-none focus:blur-none active:blur-none transition-all duration-300 bg-zinc-100 px-2 py-1 rounded text-xs text-zinc-800 cursor-pointer select-none hover:select-text"
                            title="Zbulo përkthimin"
                          >
                            {it.context || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <select
                            value={it.status}
                            disabled={busyId === it.id}
                            onChange={(e) => updateStatus(it.id, e.target.value)}
                            className="text-xs border border-zinc-200 rounded-lg px-2 py-1 bg-white cursor-pointer hover:border-zinc-300 focus:outline-none"
                          >
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            type="button"
                            disabled={busyId === it.id}
                            onClick={() => remove(it.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50/50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100/60"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Fshi
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UnknownWordsPage;
